import React, { useState, useEffect, useRef, useCallback } from 'react';
import apiService from '../../../../services/api';

// ── Helpers ────────────────────────────────────────────────────────────────
const getInitials = (email = '') => email.slice(0, 2).toUpperCase();

const timeAgo = (dateString) => {
  if (!dateString) return '';
  const diff = Date.now() - new Date(dateString).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7)  return `${d}d ago`;
  return new Date(dateString).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
};

const formatFull = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const AVATAR_COLORS = [
  '#6366f1','#0ea5e9','#10b981','#f59e0b','#ef4444',
  '#8b5cf6','#ec4899','#14b8a6','#f97316','#06b6d4',
];

const avatarColor = (email = '') => {
  let hash = 0;
  for (let c of email) hash = (hash * 31 + c.charCodeAt(0)) & 0xffffffff;
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const STATUS_META = {
  active:    { bg: '#dcfce7', color: '#16a34a', dot: '#16a34a', label: 'Active' },
  completed: { bg: '#dbeafe', color: '#2563eb', dot: '#2563eb', label: 'Done' },
  flagged:   { bg: '#fee2e2', color: '#dc2626', dot: '#dc2626', label: 'Flagged' },
};

// ── Check if a conversation ID is a direct message (non-numeric) ──────────
const isDirectMessageId = (id) =>
  typeof id === 'string' && (id.startsWith('direct_') || isNaN(Number(id)));

// ── Parse direct_<adId>_<userId> into parts ──────────────────────────────
const parseDirectId = (id) => {
  if (!isDirectMessageId(id)) return null;
  // Format: direct_<ad_id>_<other_user_id>
  const parts = String(id).replace(/^direct_/, '').split('_');
  if (parts.length >= 2) {
    return { ad_id: parts[0], user_id: parts.slice(1).join('_') };
  }
  return null;
};

// ── Group conversations by user-pair ──────────────────────────────────────
const groupByUserPair = (conversations) => {
  const map = {};
  conversations.forEach(conv => {
    const key = [conv.buyer_email, conv.seller_email].sort().join('||');
    if (!map[key]) {
      map[key] = {
        key,
        user_a: conv.buyer_email,
        user_b: conv.seller_email,
        conversations: [],
        total_messages: 0,
        last_message_time: null,
        last_message_content: '',
        has_flagged: false,
        products: new Set(),
      };
    }
    map[key].conversations.push(conv);
    map[key].total_messages += conv.message_count || 0;
    map[key].products.add(conv.product_title);
    if (conv.status === 'flagged') map[key].has_flagged = true;
    if (!map[key].last_message_time || new Date(conv.last_message_time) > new Date(map[key].last_message_time)) {
      map[key].last_message_time = conv.last_message_time;
      map[key].last_message_content = conv.last_message_content;
    }
  });
  return Object.values(map).sort(
    (a, b) => new Date(b.last_message_time) - new Date(a.last_message_time)
  );
};

// ── Avatar ─────────────────────────────────────────────────────────────────
const Avatar = ({ email, size = 36 }) => (
  <div style={{
    width: size, height: size, borderRadius: '50%',
    background: avatarColor(email),
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: 'white', fontWeight: 700, fontSize: size * 0.35,
    flexShrink: 0, letterSpacing: '-0.5px',
  }}>
    {getInitials(email)}
  </div>
);

// ── Main Component ─────────────────────────────────────────────────────────
function UserCommunications() {
  const [conversations,  setConversations]  = useState([]);
  const [stats,          setStats]          = useState({ total: 0, active: 0, completed: 0, flagged: 0 });
  const [search,         setSearch]         = useState('');
  const [statusFilter,   setStatusFilter]   = useState('all');
  const [selectedGroup,  setSelectedGroup]  = useState(null);
  const [selectedConv,   setSelectedConv]   = useState(null);
  const [convMessages,   setConvMessages]   = useState([]);
  const [loadingMsgs,    setLoadingMsgs]    = useState(false);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState(null);
  // Per-conversation flag state so the UI updates without a full refetch
  const [convFlagged,    setConvFlagged]    = useState(false);
  const [flagging,       setFlagging]       = useState(false);
  const chatEndRef = useRef(null);

  // ── computeStats — declared first so useEffects below can reference it ───
  const computeStats = useCallback((convList) => {
    const groups = groupByUserPair(convList);
    const ACTIVE_WINDOW_MS = 20 * 60 * 1000;
    const now = Date.now();
    let active = 0, done = 0, flagged = 0;
    groups.forEach(g => {
      if (g.has_flagged) {
        flagged++;
      } else if (g.last_message_time && (now - new Date(g.last_message_time).getTime()) <= ACTIVE_WINDOW_MS) {
        active++;
      } else {
        if (g.conversations.every(c => c.status === 'completed')) done++;
      }
    });
    setStats({ total: groups.length, active, completed: done, flagged });
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchConversations(); }, [statusFilter]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [convMessages]);
  useEffect(() => { if (conversations.length > 0) computeStats(conversations); }, [conversations, computeStats]);

  // Sync local flag state whenever selected conversation changes
  useEffect(() => {
    if (selectedConv) {
      setConvFlagged(selectedConv.status === 'flagged' || !!selectedConv.is_flagged);
    }
  }, [selectedConv]);

  const fetchConversations = async () => {
    try {
      setLoading(true); setError(null);
      const response = await apiService.adminGetAllConversations(statusFilter);
      if (response.success) {
        const convList = response.conversations || [];
        setConversations(convList);
        computeStats(convList);
      } else throw new Error(response.error || 'Failed to fetch');
    } catch (err) { setError(err.message); setConversations([]); }
    finally { setLoading(false); }
  };

  const fetchMessages = async (conv) => {
    setSelectedConv(conv);
    setConvFlagged(conv.status === 'flagged' || !!conv.is_flagged);
    setConvMessages([]);
    setLoadingMsgs(true);
    try {
      const r = await apiService.adminGetConversationMessages?.(conv.id);
      if (r?.success && r.messages?.length) { setConvMessages(r.messages); return; }

      const r2 = await apiService.adminGetAdMessages?.(conv.id);
      if (r2?.success && r2.messages?.length) { setConvMessages(r2.messages); return; }

      const r3 = await apiService.getAdMessages?.(conv.ad_id || conv.id);
      if (r3?.success) setConvMessages(r3.messages || []);
      else if (r3?.results) setConvMessages(r3.results || []);
    } catch (e) { console.warn('Could not load messages:', e); }
    finally { setLoadingMsgs(false); }
  };

  // ── Flag — handles both numeric offer IDs and direct_* IDs ──────────────
  const handleFlag = async (id) => {
    if (!window.confirm('Flag this conversation? Users will be unable to send messages until the flag is removed.')) return;
    setFlagging(true);
    try {
      let success = false;

      if (isDirectMessageId(id)) {
        // Direct message thread — try the dedicated DM admin endpoint
        const parsed = parseDirectId(id);
        if (parsed) {
          // Try endpoint 1: /admin/direct-messages/<ad_id>/<user_id>/flag/
          const r1 = await apiService.adminFlagDirectMessage?.(parsed.ad_id, parsed.user_id);
          if (r1?.success) { success = true; }
          else {
            // Try endpoint 2: /admin/messages/flag/ with body
            const r2 = await apiService.adminFlagConversationByIds?.(parsed.ad_id, parsed.user_id);
            if (r2?.success) success = true;
          }
        }
        if (!success) {
          alert('Could not flag this direct message thread. Please ensure the backend has a flag endpoint for direct messages (e.g. POST /api/admin/direct-messages/<ad_id>/<user_id>/flag/).');
          return;
        }
      } else {
        // Numeric offer-based conversation
        const r = await apiService.adminFlagConversation(id);
        if (r.success) success = true;
        else { alert(r.error || 'Failed to flag conversation'); return; }
      }

      if (success) {
        setConvFlagged(true);
        // Update the conversation in the local list so the thread panel pill updates too
        setConversations(prev =>
          prev.map(c => c.id === id ? { ...c, status: 'flagged', is_flagged: true } : c)
        );
        if (selectedConv?.id === id) {
          setSelectedConv(prev => ({ ...prev, status: 'flagged', is_flagged: true }));
        }
      }
    } catch { alert('Error flagging conversation'); }
    finally { setFlagging(false); }
  };

  // ── Unflag — handles both numeric and direct_* IDs ───────────────────────
  const handleUnflag = async (id) => {
    if (!window.confirm('Remove flag from this conversation? Messaging will be re-enabled.')) return;
    setFlagging(true);
    try {
      let success = false;

      if (isDirectMessageId(id)) {
        const parsed = parseDirectId(id);
        if (parsed) {
          const r1 = await apiService.adminUnflagDirectMessage?.(parsed.ad_id, parsed.user_id);
          if (r1?.success) { success = true; }
          else {
            const r2 = await apiService.adminUnflagConversationByIds?.(parsed.ad_id, parsed.user_id);
            if (r2?.success) success = true;
          }
        }
        if (!success) {
          alert('Could not unflag this direct message thread. Please ensure the backend has an unflag endpoint for direct messages.');
          return;
        }
      } else {
        // Numeric offer conversation — try dedicated unflag, fallback to complete
        const r = await apiService.adminUnflagConversation?.(id);
        if (r?.success) {
          success = true;
        } else {
          const r2 = await apiService.adminCompleteConversation(id);
          if (r2.success) success = true;
          else { alert(r2.error || 'Failed to remove flag'); return; }
        }
      }

      if (success) {
        setConvFlagged(false);
        setConversations(prev =>
          prev.map(c => c.id === id ? { ...c, status: 'active', is_flagged: false } : c)
        );
        if (selectedConv?.id === id) {
          setSelectedConv(prev => ({ ...prev, status: 'active', is_flagged: false }));
        }
      }
    } catch { alert('Error removing flag'); }
    finally { setFlagging(false); }
  };

  const handleComplete = async (id) => {
    if (!window.confirm('Mark this conversation as completed?')) return;
    try {
      const r = await apiService.adminCompleteConversation(id);
      if (r.success) { fetchConversations(); setSelectedConv(null); }
      else alert(r.error || 'Failed to complete conversation');
    } catch { alert('Error completing conversation'); }
  };

  // ── Filtered + grouped ──────────────────────────────────────────────────
  const filtered = conversations.filter(c => {
    const s = search.toLowerCase();
    return (
      c.buyer_email?.toLowerCase().includes(s) ||
      c.seller_email?.toLowerCase().includes(s) ||
      c.product_title?.toLowerCase().includes(s)
    );
  });
  const grouped = groupByUserPair(filtered);

  // ── Resizable column widths ─────────────────────────────────────────────
  const [col1Width, setCol1Width] = useState(340);
  const [col2Width, setCol2Width] = useState(280);
  const isDragging      = useRef(null);
  const dragStart       = useRef(0);
  const dragStartWidths = useRef([0, 0]);

  const onDividerMouseDown = (divider) => (e) => {
    e.preventDefault();
    isDragging.current      = divider;
    dragStart.current       = e.clientX;
    dragStartWidths.current = [col1Width, col2Width];
    document.body.style.cursor     = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMove = (ev) => {
      const delta = ev.clientX - dragStart.current;
      if (isDragging.current === 1) setCol1Width(Math.max(180, Math.min(520, dragStartWidths.current[0] + delta)));
      else setCol2Width(Math.max(160, Math.min(480, dragStartWidths.current[1] + delta)));
    };
    const onUp = () => {
      isDragging.current = null;
      document.body.style.cursor = ''; document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove); window.addEventListener('mouseup', onUp);
  };

  // ── Styles ──────────────────────────────────────────────────────────────
  const S = {
    root: { display: 'flex', height: '100vh', background: '#f8fafc', fontFamily: "'DM Sans', sans-serif", overflow: 'hidden' },
    left: { width: col1Width, flexShrink: 0, display: 'flex', flexDirection: 'column', background: 'white', borderRight: '1px solid #e2e8f0', transition: 'none' },
    leftHeader: { padding: '20px 20px 0', borderBottom: '1px solid #f1f5f9' },
    title: { fontSize: 20, fontWeight: 800, color: '#0f172a', margin: '0 0 4px' },
    subtitle: { fontSize: 12, color: '#94a3b8', margin: '0 0 16px' },
    statsRow: { display: 'flex', gap: 8, marginBottom: 16 },
    statChip: (color) => ({ flex: 1, background: '#f8fafc', borderRadius: 8, padding: '8px 6px', textAlign: 'center', borderTop: `3px solid ${color}` }),
    searchWrap: { padding: '12px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', gap: 8 },
    searchInput: { flex: 1, padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 13, outline: 'none', background: '#f8fafc' },
    select: { padding: '9px 10px', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 12, background: '#f8fafc', cursor: 'pointer', color: '#475569' },
    list: { flex: 1, overflowY: 'auto' },
    groupRow: (active) => ({
      display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 16px',
      cursor: 'pointer', borderBottom: '1px solid #f8fafc',
      background: active ? '#f0f4ff' : 'white',
      borderLeft: active ? '3px solid #6366f1' : '3px solid transparent',
      transition: 'all 0.15s',
    }),
    avatarStack: { position: 'relative', width: 44, height: 36, flexShrink: 0 },
    groupInfo: { flex: 1, minWidth: 0 },
    groupUsers: { fontSize: 13, fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    groupMeta: { fontSize: 11, color: '#94a3b8', marginTop: 2 },
    groupPreview: { fontSize: 12, color: '#64748b', marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    right: { flex: 1, display: 'flex', flexDirection: 'column', background: '#f8fafc' },
    emptyState: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' },
    thread: { width: col2Width, flexShrink: 0, background: 'white', borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', transition: 'none' },
    threadHeader: { padding: '16px', borderBottom: '1px solid #f1f5f9' },
    convRow: (active) => ({ padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #f8fafc', background: active ? '#f0f4ff' : 'white', transition: 'background 0.15s' }),
    convProduct: { fontSize: 13, fontWeight: 600, color: '#1e293b' },
    convSub: { fontSize: 11, color: '#94a3b8', marginTop: 2 },
    chat: { flex: 1, display: 'flex', flexDirection: 'column' },
    chatMessages: { flex: 1, overflowY: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 3, background: '#e5ddd5' },
    msgBubble: (isBuyer) => ({
      maxWidth: '72%', minWidth: 130,
      background: isBuyer ? '#ffffff' : '#dcf8c6',
      color: '#111b21', padding: '7px 10px 5px 10px',
      borderRadius: isBuyer ? '0px 10px 10px 10px' : '10px 0px 10px 10px',
      boxShadow: '0 1px 1px rgba(0,0,0,0.13)', position: 'relative',
    }),
    chatActions: { padding: '12px 20px', background: 'white', borderTop: '1px solid #e2e8f0', display: 'flex', gap: 10 },
    actionBtn: (bg, color, disabled = false) => ({
      flex: 1, padding: '9px 12px',
      background: disabled ? '#f1f5f9' : bg,
      color: disabled ? '#94a3b8' : color,
      border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1,
    }),
  };

  return (
    <div style={S.root}>

      {/* ── Left: User-pair list ───────────────────────────────────────── */}
      <div style={S.left}>
        <div style={S.leftHeader}>
          <h2 style={S.title}>💬 Communications</h2>
          <p style={S.subtitle}>Grouped by user pair · {grouped.length} pair{grouped.length !== 1 ? 's' : ''} · Active = last msg ≤ 20 min</p>
          <div style={S.statsRow}>
            {[
              { label: 'Groups',  value: stats.total,     color: '#6366f1' },
              { label: 'Active',  value: stats.active,    color: '#16a34a' },
              { label: 'Done',    value: stats.completed, color: '#2563eb' },
              { label: 'Flagged', value: stats.flagged,   color: '#ef4444' },
            ].map(s => (
              <div key={s.label} style={S.statChip(s.color)}>
                <div style={{ fontSize: 18, fontWeight: 800, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 1 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={S.searchWrap}>
          <input style={S.searchInput} placeholder="Search users, products..." value={search} onChange={e => setSearch(e.target.value)} />
          <select style={S.select} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="completed">Done</option>
            <option value="flagged">Flagged</option>
          </select>
        </div>

        <div style={S.list}>
          {loading && <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Loading conversations...</div>}
          {error && !loading && (
            <div style={{ padding: 16, background: '#fee2e2', color: '#dc2626', fontSize: 12, margin: 12, borderRadius: 8 }}>
              {error}
              <button onClick={fetchConversations} style={{ marginLeft: 8, textDecoration: 'underline', background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer' }}>Retry</button>
            </div>
          )}
          {!loading && !error && grouped.map(group => {
            const isActive    = selectedGroup?.key === group.key;
            const productList = [...group.products].filter(Boolean).slice(0, 2).join(', ');
            return (
              <div key={group.key} style={S.groupRow(isActive)}
                onClick={() => { setSelectedGroup(group); setSelectedConv(null); setConvMessages([]); }}
              >
                <div style={S.avatarStack}>
                  <div style={{ position: 'absolute', top: 0, left: 0 }}><Avatar email={group.user_a} size={30} /></div>
                  <div style={{ position: 'absolute', top: 8, left: 16, border: '2px solid white', borderRadius: '50%' }}><Avatar email={group.user_b} size={26} /></div>
                </div>
                <div style={S.groupInfo}>
                  <div style={S.groupUsers}>{group.user_a?.split('@')[0]} ↔ {group.user_b?.split('@')[0]}</div>
                  <div style={S.groupMeta}>
                    {group.conversations.length} thread{group.conversations.length !== 1 ? 's' : ''} ·{' '}
                    {group.total_messages} msg{group.total_messages !== 1 ? 's' : ''} ·{' '}
                    {productList || 'various'}
                    {group.has_flagged && <span style={{ color: '#ef4444', marginLeft: 4 }}>🚩</span>}
                  </div>
                  <div style={S.groupPreview}>{group.last_message_content || 'No messages yet'}</div>
                </div>
                <div style={{ fontSize: 10, color: '#94a3b8', flexShrink: 0, marginTop: 2, textAlign: 'right' }}>
                  {timeAgo(group.last_message_time)}
                  <div style={{ marginTop: 4 }}>
                    <span style={{ background: group.has_flagged ? '#fee2e2' : '#dcfce7', color: group.has_flagged ? '#dc2626' : '#16a34a', padding: '1px 6px', borderRadius: 20, fontSize: 9, fontWeight: 700 }}>
                      {group.has_flagged ? 'FLAGGED' : 'OK'}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
          {!loading && !error && grouped.length === 0 && (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No conversations found.</div>
          )}
        </div>
      </div>

      {/* ── Divider 1 ─────────────────────────────────────────────────── */}
      <div onMouseDown={onDividerMouseDown(1)} style={{ width: 5, flexShrink: 0, cursor: 'col-resize', background: 'transparent', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Drag to resize">
        <div style={{ width: 3, height: 40, borderRadius: 3, background: '#cbd5e1' }} />
      </div>

      {/* ── Right side ────────────────────────────────────────────────── */}
      {!selectedGroup ? (
        <div style={{ ...S.right, ...S.emptyState }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>💬</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#334155' }}>Select a conversation</div>
          <div style={{ fontSize: 13, marginTop: 6 }}>Choose a user pair from the left to view their threads</div>
        </div>
      ) : (
        <div style={{ ...S.right, flexDirection: 'row' }}>

          {/* ── Thread list ──────────────────────────────────────────── */}
          <div style={S.thread}>
            <div style={S.threadHeader}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10 }}>
                <Avatar email={selectedGroup.user_a} size={32} />
                <Avatar email={selectedGroup.user_b} size={32} />
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{selectedGroup.user_a?.split('@')[0]}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>↔ {selectedGroup.user_b?.split('@')[0]}</div>
                </div>
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8', background: '#f8fafc', borderRadius: 6, padding: '6px 8px' }}>
                📦 {selectedGroup.conversations.length} product thread{selectedGroup.conversations.length !== 1 ? 's' : ''} · {selectedGroup.total_messages} total messages
              </div>
            </div>

            <div style={{ flex: 1, overflowY: 'auto' }}>
              {selectedGroup.conversations.map((conv) => {
                const isDirect = isDirectMessageId(conv.id);
                // Use live convFlagged for the selected conv, otherwise use conv.status
                const isFlaggedThread = selectedConv?.id === conv.id
                  ? convFlagged
                  : (conv.status === 'flagged' || !!conv.is_flagged);
                const smKey  = isFlaggedThread ? 'flagged' : (conv.status || 'active');
                const sm     = STATUS_META[smKey] || STATUS_META.active;
                const isActive = selectedConv?.id === conv.id;
                return (
                  <div key={conv.id}
                    style={{ ...S.convRow(isActive), borderLeft: isActive ? '3px solid #6366f1' : '3px solid transparent' }}
                    onClick={() => fetchMessages(conv)}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={S.convProduct}>
                        {isDirect ? '💬' : '📦'} {conv.product_title || (isDirect ? 'Direct Message' : 'Unknown product')}
                      </div>
                      <span style={{ background: sm.bg, color: sm.color, padding: '2px 7px', borderRadius: 20, fontSize: 9, fontWeight: 700, marginLeft: 6, flexShrink: 0 }}>
                        {sm.label}
                      </span>
                    </div>
                    <div style={S.convSub}>
                      {conv.message_count || 0} messages · {timeAgo(conv.last_message_time)}
                      {isDirect && <span style={{ marginLeft: 6, color: '#94a3b8', fontStyle: 'italic' }}>(direct)</span>}
                    </div>
                    {conv.last_message_content && (
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {conv.last_message_content}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Divider 2 ────────────────────────────────────────────── */}
          <div onMouseDown={onDividerMouseDown(2)} style={{ width: 5, flexShrink: 0, cursor: 'col-resize', background: 'transparent', position: 'relative', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Drag to resize">
            <div style={{ width: 3, height: 40, borderRadius: 3, background: '#cbd5e1' }} />
          </div>

          {/* ── Chat view ───────────────────────────────────────────── */}
          {!selectedConv ? (
            <div style={{ flex: 1, ...S.emptyState }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>👆</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#475569' }}>Select a product thread</div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{selectedGroup.conversations.length} thread{selectedGroup.conversations.length !== 1 ? 's' : ''} available</div>
            </div>
          ) : (
            <div style={S.chat}>
              {/* ── WhatsApp-style Chat Header ── */}
              <div style={{ padding: '10px 16px', background: '#075e54', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ position: 'relative', width: 42, height: 38, flexShrink: 0 }}>
                    <div style={{ position: 'absolute', top: 0, left: 0 }}><Avatar email={selectedConv.buyer_email} size={28} /></div>
                    <div style={{ position: 'absolute', top: 10, left: 16, border: '2px solid #075e54', borderRadius: '50%' }}><Avatar email={selectedConv.seller_email} size={24} /></div>
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'white' }}>
                      {selectedConv.product_title || (isDirectMessageId(selectedConv.id) ? 'Direct Message' : 'Product conversation')}
                      {convFlagged && <span style={{ marginLeft: 8, fontSize: 10, background: 'rgba(239,68,68,0.25)', color: '#fca5a5', padding: '2px 7px', borderRadius: 8, fontWeight: 700 }}>🚩 Flagged</span>}
                    </div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.75)', marginTop: 1 }}>
                      {selectedConv.buyer_email?.split('@')[0]} &amp; {selectedConv.seller_email?.split('@')[0]} · {selectedConv.message_count || 0} messages
                      {isDirectMessageId(selectedConv.id) && <span style={{ marginLeft: 6, opacity: 0.7 }}>(direct msg)</span>}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {(() => {
                    const smKey = convFlagged ? 'flagged' : (selectedConv.status || 'active');
                    const sm = STATUS_META[smKey] || STATUS_META.active;
                    return (
                      <span style={{ background: 'rgba(255,255,255,0.15)', color: 'white', padding: '3px 10px', borderRadius: 20, fontSize: 10, fontWeight: 700, border: '1px solid rgba(255,255,255,0.3)' }}>
                        ● {sm.label}
                      </span>
                    );
                  })()}
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', textAlign: 'right' }}>
                    <div>Started {formatFull(selectedConv.created_at)}</div>
                  </div>
                </div>
              </div>

              {/* ── Messages ────────────────────────────────────────── */}
              <div style={S.chatMessages}>
                {loadingMsgs && (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: 30 }}>
                    <div style={{ background: 'rgba(255,255,255,0.85)', borderRadius: 20, padding: '8px 18px', fontSize: 12, color: '#667781', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>Loading messages...</div>
                  </div>
                )}

                {!loadingMsgs && convMessages.length === 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 10 }}>
                    <div style={{ background: 'rgba(255,255,255,0.85)', borderRadius: 12, padding: '20px 28px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', maxWidth: 340 }}>
                      <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#111b21', marginBottom: 6 }}>Message history not available</div>
                      <div style={{ fontSize: 12, color: '#667781', lineHeight: 1.5 }}>
                        This thread has <strong>{selectedConv.message_count || 0}</strong> message(s) recorded in the database.
                        Wire up <code style={{ background: '#f0f2f5', padding: '1px 5px', borderRadius: 4, fontSize: 11 }}>adminGetConversationMessages</code> in your API service to display them here.
                      </div>
                      {selectedConv.last_message_content && (
                        <div style={{ marginTop: 12, padding: '10px 12px', background: '#f0f2f5', borderRadius: 8, textAlign: 'left' }}>
                          <div style={{ fontSize: 10, color: '#667781', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Last message</div>
                          <div style={{ fontSize: 13, color: '#111b21' }}>"{selectedConv.last_message_content}"</div>
                          <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>{formatFull(selectedConv.last_message_time)}</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {!loadingMsgs && convMessages.length > 0 && (() => {
                  const items = [];
                  let lastDate = null;
                  convMessages.forEach((msg, i) => {
                    const msgDate   = msg.created_at ? new Date(msg.created_at) : null;
                    const shortDate = msgDate ? msgDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : null;
                    const timeStr   = msgDate ? msgDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }) : '';
                    if (shortDate && shortDate !== lastDate) {
                      lastDate = shortDate;
                      items.push(
                        <div key={`date-${i}`} style={{ display: 'flex', justifyContent: 'center', margin: '10px 0 6px' }}>
                          <div style={{ background: 'rgba(255,255,255,0.85)', borderRadius: 8, padding: '4px 12px', fontSize: 11, color: '#667781', boxShadow: '0 1px 1px rgba(0,0,0,0.1)', fontWeight: 600 }}>{shortDate}</div>
                        </div>
                      );
                    }
                    const isBuyer    = msg.sender_email === selectedConv.buyer_email || msg.sender === selectedConv.buyer_email || msg.sender_role === 'buyer';
                    const senderName = (msg.sender_email || msg.sender || '').split('@')[0];
                    const msgText    = msg.message || msg.content || msg.text || '';
                    items.push(
                      <div key={msg.id || i} style={{ display: 'flex', justifyContent: isBuyer ? 'flex-start' : 'flex-end', marginBottom: 4, paddingLeft: isBuyer ? 0 : 48, paddingRight: isBuyer ? 48 : 0 }}>
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, flexDirection: isBuyer ? 'row' : 'row-reverse', maxWidth: '100%' }}>
                          <div style={{ flexShrink: 0, marginBottom: 4 }}><Avatar email={msg.sender_email || msg.sender || ''} size={28} /></div>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: isBuyer ? 'flex-start' : 'flex-end' }}>
                            {(i === 0 || (convMessages[i - 1] && (convMessages[i - 1].sender_email || convMessages[i - 1].sender) !== (msg.sender_email || msg.sender))) && (
                              <div style={{ fontSize: 11, fontWeight: 700, color: avatarColor(msg.sender_email || msg.sender || ''), marginBottom: 2, paddingLeft: isBuyer ? 2 : 0, paddingRight: isBuyer ? 0 : 2 }}>
                                {senderName} {isBuyer ? '· Buyer' : '· Seller'}
                              </div>
                            )}
                            <div style={S.msgBubble(isBuyer)}>
                              <div style={{ position: 'absolute', top: 0, [isBuyer ? 'left' : 'right']: -6, width: 0, height: 0, borderStyle: 'solid', borderWidth: isBuyer ? '0 8px 8px 0' : '0 0 8px 8px', borderColor: isBuyer ? 'transparent #ffffff transparent transparent' : 'transparent transparent transparent #dcf8c6' }} />
                              <div style={{ fontSize: 13.5, lineHeight: 1.6, color: '#111b21', wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'pre-wrap' }}>{msgText}</div>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 3, marginTop: 3 }}>
                                <span style={{ fontSize: 10, color: '#667781', whiteSpace: 'nowrap' }}>{timeStr}</span>
                                {!isBuyer && (
                                  <svg width="15" height="11" viewBox="0 0 15 11" fill="none">
                                    <path d="M1 6l3 3 5-5" stroke={msg.is_read ? '#53bdeb' : '#8696a0'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M5 6l3 3 5-5" stroke={msg.is_read ? '#53bdeb' : '#8696a0'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  });
                  return items;
                })()}
                <div ref={chatEndRef} />
              </div>

              {/* ── Flagged warning banner ──────────────────────────── */}
              {convFlagged && (
                <div style={{ background: '#fff1f2', borderTop: '2px solid #fecdd3', padding: '10px 20px', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 18 }}>🚩</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#be123c' }}>This conversation has been flagged</div>
                    <div style={{ fontSize: 11, color: '#9f1239', marginTop: 1 }}>Messaging is frozen for users. Remove the flag once the issue is resolved.</div>
                  </div>
                  <button
                    disabled={flagging}
                    style={{ padding: '6px 14px', background: '#be123c', color: 'white', border: 'none', borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: flagging ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', opacity: flagging ? 0.6 : 1 }}
                    onClick={() => handleUnflag(selectedConv.id)}
                  >
                    {flagging ? '…' : '✕ Remove Flag'}
                  </button>
                </div>
              )}

              {/* ── Actions ─────────────────────────────────────────── */}
              <div style={S.chatActions}>
                {/* Flag / unflag — both work for direct and offer conversations */}
                {!convFlagged ? (
                  <button
                    style={S.actionBtn('#fee2e2', '#dc2626', flagging)}
                    onClick={() => handleFlag(selectedConv.id)}
                    disabled={flagging}
                    title="Flag this conversation — users will be unable to message"
                  >
                    {flagging ? '…' : '🚩 Flag Chat'}
                  </button>
                ) : (
                  <button
                    style={S.actionBtn('#fff1f2', '#be123c', flagging)}
                    onClick={() => handleUnflag(selectedConv.id)}
                    disabled={flagging}
                    title="Remove flag and re-enable messaging"
                  >
                    {flagging ? '…' : '✕ Remove Flag'}
                  </button>
                )}

                {selectedConv.status !== 'completed' && (
                  <button style={S.actionBtn('#dbeafe', '#2563eb')} onClick={() => handleComplete(selectedConv.id)}>
                    ✓ Mark Done
                  </button>
                )}

                <button
                  style={S.actionBtn('#f1f5f9', '#475569')}
                  onClick={() => {
                    const data = [
                      `Product: ${selectedConv.product_title}`,
                      `Buyer: ${selectedConv.buyer_email}`,
                      `Seller: ${selectedConv.seller_email}`,
                      `Messages: ${selectedConv.message_count}`,
                      `Last: ${selectedConv.last_message_content}`,
                      `Time: ${formatFull(selectedConv.last_message_time)}`,
                    ].join('\n');
                    const blob = new Blob([data], { type: 'text/plain' });
                    const url  = URL.createObjectURL(blob);
                    const a    = document.createElement('a');
                    a.href = url; a.download = `chat-${selectedConv.id}.txt`;
                    a.click(); URL.revokeObjectURL(url);
                  }}
                >
                  📥 Export
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default UserCommunications;








